import { useEffect, useRef } from 'react';
import { useAnalytics } from '@listening/frontend/analytics/client';
import type { PurchasePixelFields } from '@/lib/checkout/post-purchase-pixels';
import { normalizeCurrency } from '@/seo/cookie-trackers/purchase/currency';

/**
 * The typed purchase signal for funnels and experiments, under the SAME gate as
 * the conversion pixels in a Stripe return page's `head()`: only a
 * Stripe-confirmed outcome reports. Declined redirects, ghost URLs and backend
 * errors resolve to `{ amountUsd: null, trialStarted: false }` in the loader and
 * emit nothing.
 */
export function usePurchaseCompletedEvent({
  amountUsd,
  currency,
  trialStarted,
}: PurchasePixelFields) {
  const { capture } = useAnalytics();
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || (amountUsd === null && !trialStarted)) return;
    reported.current = true;
    capture('purchase_completed', {
      amount_usd: amountUsd,
      currency: normalizeCurrency(currency),
      trial_started: trialStarted,
    });
  }, [amountUsd, currency, trialStarted, capture]);
}
